import React, { useState, useEffect } from 'react';
import '../App-modern.css';
import { useScope } from '../contexts/ScopeContext';

// Import inventory components (we'll create these next)
import ItemsManagement from '../components/inventory/ItemsManagement';
import SuppliersManagement from '../components/inventory/SuppliersManagement';
import InventoryReceiving from '../components/inventory/InventoryReceiving';
import StockLevels from '../components/inventory/StockLevels';
import InventoryReports from '../components/inventory/InventoryReports';
import RecipeManagement from '../components/inventory/RecipeManagement';
// Intelligent inventory additions
import DemandForecast from '../components/inventory/DemandForecast';
import PurchaseOrdersDashboard from '../components/inventory/PurchaseOrdersDashboard';
import InvoiceScanner from '../components/inventory/InvoiceScanner';
import WasteAnalytics from '../components/inventory/WasteAnalytics';
import NetworkAlerts from '../components/inventory/NetworkAlerts';
import VisionZoneConfig from '../components/inventory/VisionZoneConfig';

interface InventoryManagementProps {
  user: any;
}

type InventoryTab =
  | 'stock'
  | 'items'
  | 'recipes'
  | 'suppliers'
  | 'receiving'
  | 'forecast'
  | 'orders'
  | 'invoices'
  | 'waste'
  | 'alerts'
  | 'vision'
  | 'reports';

const InventoryManagement: React.FC<InventoryManagementProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<InventoryTab>('stock');
  const { storeId: scopeStoreId, enterpriseId: scopeEnterpriseId, ready: scopeReady } = useScope();
  const selectedStore = scopeStoreId || '';
  const [loading, setLoading] = useState(true);
  // session from useAuth isn't needed here anymore; scope handles auth context.

  useEffect(() => {
    // Scope provider handles the store list now; just flip loading off when it's ready.
    if (scopeReady) setLoading(false);
  }, [scopeReady]);

  const tabs = [
    { id: 'stock' as InventoryTab, label: 'Current Stock' },
    { id: 'receiving' as InventoryTab, label: 'Receiving' },
    { id: 'recipes' as InventoryTab, label: 'Recipes' },
    { id: 'items' as InventoryTab, label: 'Items Catalog' },
    { id: 'suppliers' as InventoryTab, label: 'Suppliers' },
    { id: 'forecast' as InventoryTab, label: 'Forecast' },
    { id: 'orders' as InventoryTab, label: 'Purchase Orders' },
    { id: 'invoices' as InventoryTab, label: 'Invoices' },
    { id: 'waste' as InventoryTab, label: 'Waste' },
    { id: 'alerts' as InventoryTab, label: 'Network Alerts' },
    { id: 'vision' as InventoryTab, label: 'Vision Zones' },
    { id: 'reports' as InventoryTab, label: 'Reports' },
  ];

  const renderActiveComponent = () => {
    const commonProps = {
      user,
      selectedStore,
      enterpriseId: scopeEnterpriseId || '',
    };

    switch (activeTab) {
      case 'items':
        return <ItemsManagement {...commonProps} />;
      case 'suppliers':
        return <SuppliersManagement {...commonProps} />;
      case 'receiving':
        return <InventoryReceiving {...commonProps} />;
      case 'stock':
        return <StockLevels {...commonProps} />;
      case 'recipes':
        return <RecipeManagement {...commonProps} />;
      case 'reports':
        return <InventoryReports {...commonProps} />;
      case 'forecast':
        return <DemandForecast {...commonProps} />;
      case 'orders':
        return <PurchaseOrdersDashboard {...commonProps} />;
      case 'invoices':
        return <InvoiceScanner {...commonProps} />;
      case 'waste':
        return <WasteAnalytics {...commonProps} />;
      case 'alerts':
        return <NetworkAlerts {...commonProps} />;
      case 'vision':
        return <VisionZoneConfig {...commonProps} />;
      default:
        return <StockLevels {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="main-body">
        <div className="skeleton" style={{height: '200px', borderRadius: '12px'}}></div>
        <p>Loading inventory system...</p>
      </div>
    );
  }

  return (
    <div className="main-body">
      {/* Store selection lives in the header strip — no in-page picker. */}

      {/* Navigation Tabs */}
      <div className="modern-card" style={{marginBottom: 'var(--space-lg)', padding: 'var(--space-md)'}}>
        <div style={{display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap'}}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`btn-modern ${activeTab === tab.id ? 'btn-modern-accent' : 'btn-modern-secondary'}`}
              onClick={() => setActiveTab(tab.id)}
              style={{padding: '0.75rem 1.5rem', fontSize: '0.875rem', fontWeight: '500'}}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 280px', gap: 'var(--space-lg)'}}>
        <div className="modern-card">
          {selectedStore ? (
            renderActiveComponent()
          ) : (
            <div style={{textAlign: 'center', padding: 'var(--space-3xl)'}}>
              <h3 style={{color: 'var(--text-secondary)', marginBottom: 'var(--space-md)'}}>No Store Selected</h3>
              <p style={{color: 'var(--text-tertiary)'}}>Please select a store to manage inventory.</p>
            </div>
          )}
        </div>

        {/* Quick Actions Sidebar */}
        <div className="modern-card" style={{height: 'fit-content'}}>
          <h3 style={{marginBottom: 'var(--space-lg)', fontSize: '1.125rem', fontWeight: '600'}}>Quick Actions</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)'}}>
          <button 
            className="btn-modern-accent"
            onClick={() => setActiveTab('receiving')}
          >
            Receive Delivery
          </button>
          <button 
            className="btn-modern-secondary"
            onClick={() => setActiveTab('recipes')}
          >
            Manage Recipes
          </button>
          <button 
            className="btn-modern-secondary"
            onClick={() => setActiveTab('items')}
          >
            Add New Item
          </button>
          <button 
            className="btn-modern-secondary"
            onClick={() => setActiveTab('stock')}
          >
            Check Stock Levels
          </button>
          <button 
            className="btn-modern-secondary"
            onClick={() => setActiveTab('reports')}
          >
            Expiring Items
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;